import * as React from 'react';
import { makeStyles } from '@mui/styles';
import { useTheme } from 'next-themes';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';
import moment from 'moment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Avatar from '@components/Avatar/Avatar';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CustomSkeleton from '@components/Skeleton/Skeleton';
import SettingsMenu from './settings.menu/settings.menu';

function createData(userInfo, lastLogin, role, status) {
  return { userInfo, lastLogin, role, status };
}

const USER_STATUSES = {
  accepted: 'active',
};

export default function TeamTable({
  teamMembers,
  handleDropDownOption,
  selectedTeam,
  loading,
  setModalType,
  handleModalOpen,
}) {
  const users = teamMembers?.map((member) =>
    createData(
      {
        name: member.name,
        email: member.email,
        id: member._id,
        memberId: member?.userId,
      },
      member.lastLogin,
      member.role,
      member.status
    )
  );
  const { theme } = useTheme();
  const useStyles = makeStyles({
    bodyBorder: {
      // borderBottom: theme === 'dark' ? '1px solid #1E2628' : '',
      borderBottom:
        theme === 'dark' ? '1px solid #1E2628' : '1px solid  #EBEBEF',
    },
  });
  const classes = useStyles();

  return (
    <Box
      className='bg-white rounded-md overflow-y-scroll dark:bg-header-black h-full w-full dark:border-1 dark:border-custom-border'
      border='1px solid #EBEBEF'
    >
      <div className='px-[16px] pt-[16px] font-bold'>Team Members</div>
      <TableContainer>
        <Table
          sx={{ minWidth: 700, overflowX: 'scroll' }}
          aria-label='simple table'
        >
          <TableHead>
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell sx={{ width: 265 }}>
                <div className='font-weight-custom text-custom-font-color dark:text-white'>
                  USERS
                </div>
              </TableCell>
              <TableCell
                // sx={{ width: 135 }}
                align='center'
              >
                <div className='font-weight-custom text-custom-font-color dark:text-white'>
                  LAST SIGNED IN
                </div>
              </TableCell>
              <TableCell
                // sx={{ width: 100 }}
                align='center'
              >
                <div className='font-weight-custom text-custom-font-color dark:text-white'>
                  ROLE
                </div>
              </TableCell>
              <TableCell
                // sx={{ width: 100 }}
                align='center'
              >
                <div className='font-weight-custom text-custom-font-color dark:text-white'>
                  STATUS
                </div>
              </TableCell>
              {selectedTeam?.owner ? (
                <TableCell
                  // sx={{ width: 100 }}
                  align='center'
                  className='font-weight-custom text-custom-font-color dark:text-white'
                >
                  ACTION
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          {!loading ? (
            <TableBody>
              {users?.map((user) => (
                <TableRow
                  key={user.name}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell
                    component='th'
                    scope='row'
                    classes={{ root: classes.bodyBorder }}
                  >
                    <div className='flex items-center text-custom-font-color dark:text-white'>
                      <Avatar
                        picture={user?.userInfo?.picture}
                        title={user?.userInfo?.name}
                        alt='user'
                      />
                      <div className='pl-2'>
                        <div className='font-bold text-md truncate'>
                          {user.userInfo.name}
                        </div>
                        {selectedTeam?.owner ? (
                          <div className='text-sm truncate'>
                            {user.userInfo.email}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    align='center'
                    classes={{ root: classes.bodyBorder }}
                  >
                    <div className='text-custom-font-color dark:text-white'>
                      {user.lastLogin === '-' || user.role === 'invited'
                        ? '-'
                        : moment(user.lastLogin).fromNow()}
                    </div>
                  </TableCell>
                  <TableCell
                    align='center'
                    classes={{ root: classes.bodyBorder }}
                  >
                    <div className='text-custom-font-color dark:text-white'>
                      {user.role}
                    </div>
                  </TableCell>
                  <TableCell
                    align='center'
                    classes={{ root: classes.bodyBorder }}
                  >
                    <div className=' text-custom-font-color dark:text-white'>
                      <FiberManualRecordIcon
                        sx={{ color: '#2BAE68', fontSize: '13px' }}
                      />
                      {['owner'].includes(user.role) ? (
                        <span className='pl-1'>Active</span>
                      ) : (
                        <span className='pl-1 capitalize'>
                          {USER_STATUSES[user?.status] || user?.status}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  {selectedTeam?.owner ? (
                    <TableCell
                      align='center'
                      classes={{ root: classes.bodyBorder }}
                    >
                      <div className=' text-custom-font-color dark:text-white cursor-pointer'>
                        <SettingsMenu
                          handleDropDownOption={handleDropDownOption}
                          selectedTeam={selectedTeam}
                          memberId={user?.userInfo?.id}
                          id={user?.userInfo?.memberId}
                          userRole={user?.role}
                          userName={user?.userInfo?.name}
                          setModalType={setModalType}
                          handleModalOpen={handleModalOpen}
                        />
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component='th' scope='row'>
                <div className='flex items-center'>
                  <CustomSkeleton variant='circular' width={40} height={40} />
                  <div>
                    <CustomSkeleton
                      variant='text'
                      width={110}
                      height={25}
                      style={{ marginLeft: 1 }}
                    />
                    {selectedTeam?.owner ? (
                      <CustomSkeleton
                        variant='text'
                        width={140}
                        height={15}
                        style={{ marginLeft: 1 }}
                      />
                    ) : null}
                  </div>
                </div>
              </TableCell>
              <TableCell align='center'>
                <div className='flex justify-center'>
                  <CustomSkeleton variant='text' width={70} />
                </div>
              </TableCell>
              <TableCell align='center'>
                <div className='flex justify-center'>
                  <CustomSkeleton variant='text' width={50} />
                </div>
              </TableCell>
              <TableCell align='center'>
                <div className='flex justify-center'>
                  <CustomSkeleton variant='text' width={50} />
                </div>
              </TableCell>
              {selectedTeam?.owner ? (
                <TableCell align='center'>
                  <div className='flex justify-center'>
                    <CustomSkeleton variant='text' width={50} />
                  </div>
                </TableCell>
              ) : null}
            </TableRow>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}

TeamTable.propTypes = {
  teamMembers: PropTypes.array,
  handleDropDownOption: PropTypes.func,
  setModalType: PropTypes.func,
  handleModalOpen: PropTypes.func,
  selectedTeam: PropTypes.object,
  loading: PropTypes.string,
};
